import React, { useEffect, useRef, useState } from 'react';
import { Heading } from '../components/heading';
import { Text } from '../components/text';
import { Button } from '../components/button';
import { useUser } from '../components/UserContext';
import { useOrgMiembros } from '../hooks/useOrgMiembros';
import { useOtConfig } from '../hooks/useOrdenesTrabajo';
import { AsignadosBubbles } from '../components/AsignadoBubble';
import { logBitacora } from '../utils/bitacora';
import { useOrdenesTrabajoRaw } from '../contexts/DataContext';
import {
  esTipoBase, idDesdeNombre, validarConfigOt, diasObjetivoTotal,
} from '../utils/ordenesTrabajo';
import { ArrowUpIcon, ArrowDownIcon, XMarkIcon } from '@heroicons/react/24/outline';

const field =
  'rounded-lg border border-gray-300 px-3 py-1.5 text-sm outline-none ' +
  'focus:border-azul-surelia focus:ring-1 focus:ring-azul-surelia';

/**
 * One flow's fases: ordered, each with an owning equipo and días objetivo.
 */
function TablaFases({ flujo, equipos, enUso, onChange, onRename, onDelete }) {
  const [nueva, setNueva] = useState('');
  const fases = flujo.fases;
  const total = diasObjetivoTotal(fases);
  const base = esTipoBase(flujo.id);

  const agregar = () => {
    const n = nueva.trim();
    if (!n) return;
    onChange([...fases, { id: idDesdeNombre(n, fases.map((f) => f.id)), nombre: n, equipoId: null, diasObjetivo: null, requiereAprobacion: false }]);
    setNueva('');
  };
  const patch = (id, cambios) => onChange(fases.map((f) => (f.id === id ? { ...f, ...cambios } : f)));
  const quitar = (id) => onChange(fases.filter((f) => f.id !== id));
  const mover = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= fases.length) return;
    const next = [...fases];
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={flujo.nombre}
            onChange={(ev) => onRename(ev.target.value)}
            className={`${field} w-56 font-semibold`}
            title="Nombre del tipo de orden"
          />
          {base ? (
            <span className="text-xs text-zinc-400">tipo base</span>
          ) : (
            <button
              title={enUso ? `Hay ${enUso} órdenes de este tipo` : 'Quitar tipo de orden'}
              onClick={onDelete}
              disabled={enUso > 0}
              className="p-1.5 rounded hover:bg-zinc-100 text-zinc-400 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
        </div>
        <span className="text-xs text-zinc-500 tabular-nums">
          {fases.length} fases{total ? ` · ${total} d objetivo en total` : ''}
        </span>
      </div>
      <div className="bg-white rounded-lg border border-zinc-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-azul-surelia text-left text-xs font-medium text-white">
              <th className="px-3 py-2 w-16">Orden</th>
              <th className="px-3 py-2">Fase</th>
              <th className="px-3 py-2">Equipo responsable</th>
              <th className="px-3 py-2 w-32">Días objetivo</th>
              <th className="px-3 py-2 w-28 text-center">Aprobación</th>
              <th className="px-3 py-2 w-12" />
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {fases.map((f, i) => (
              <tr key={f.id}>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-0.5">
                    <button title="Subir" onClick={() => mover(i, -1)} disabled={i === 0} className="p-1 rounded hover:bg-zinc-100 text-zinc-400 disabled:opacity-30">
                      <ArrowUpIcon className="w-3.5 h-3.5" />
                    </button>
                    <button title="Bajar" onClick={() => mover(i, 1)} disabled={i === fases.length - 1} className="p-1 rounded hover:bg-zinc-100 text-zinc-400 disabled:opacity-30">
                      <ArrowDownIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
                <td className="px-3 py-2">
                  <input type="text" value={f.nombre} onChange={(ev) => patch(f.id, { nombre: ev.target.value })} className={`${field} w-full`} />
                </td>
                <td className="px-3 py-2">
                  <select value={f.equipoId || ''} onChange={(ev) => patch(f.id, { equipoId: ev.target.value || null })} className={`${field} w-full`}>
                    <option value="">Sin equipo</option>
                    {equipos.map((e) => <option key={e.id} value={e.id}>{e.nombre || e.id}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={f.diasObjetivo ?? ''}
                    onChange={(ev) => patch(f.id, { diasObjetivo: ev.target.value === '' ? null : Number(ev.target.value) })}
                    placeholder="—"
                    className={`${field} w-full text-right tabular-nums`}
                  />
                </td>
                <td className="px-3 py-2 text-center">
                  <input
                    type="checkbox"
                    checked={Boolean(f.requiereAprobacion)}
                    onChange={(ev) => patch(f.id, { requiereAprobacion: ev.target.checked })}
                    title="Requiere aprobación de un aprobador"
                    className="rounded border-zinc-300 text-azul-surelia"
                  />
                </td>
                <td className="px-3 py-2 text-right">
                  <button title="Quitar fase" onClick={() => quitar(f.id)} className="p-1.5 rounded hover:bg-zinc-100 text-zinc-400">
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            <tr>
              <td className="px-3 py-2" colSpan={6}>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={nueva}
                    onChange={(ev) => setNueva(ev.target.value)}
                    onKeyDown={(ev) => { if (ev.key === 'Enter') agregar(); }}
                    placeholder="Nueva fase"
                    className={`${field} w-64`}
                  />
                  <Button outline color="surelia" onClick={agregar} disabled={!nueva.trim()}>Agregar</Button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * Configuración → Órdenes de trabajo. Owner-only (gated in Configuracion.js
 * and by the rules: orgs/{org}/config/* is owner-write).
 *
 * Equipos (name + members) and the org's tipos de orden, each a flow of
 * fases: fianza and endoso are the base tipos (renamable, not deletable),
 * the owner adds any other. Each fase has an owning equipo (notified when an
 * OT enters it) and días objetivo (the tarjeta and the stats compare against
 * it). A tipo with OTs cannot be deleted. Every change autosaves (debounced);
 * the defaults show until the first write.
 */
const ConfiguracionOrdenesTrabajo = () => {
  const { user, userData } = useUser();
  const { miembros } = useOrgMiembros();
  const { config, loading, guardar } = useOtConfig();
  const { ordenesTrabajo } = useOrdenesTrabajoRaw();

  const [equipos, setEquipos] = useState([]);
  const [flujos, setFlujos] = useState([]);
  const [coordinadores, setCoordinadores] = useState([]);
  const [aprobadores, setAprobadores] = useState([]);
  const [nuevoEquipo, setNuevoEquipo] = useState('');
  const [nuevoTipo, setNuevoTipo] = useState('');
  const [sucio, setSucio] = useState(false);
  const [estado, setEstado] = useState('limpio'); // limpio | pendiente | guardando | guardado | error
  const [error, setError] = useState(null);
  const timerRef = useRef(null);

  // Load from the live config until the owner starts editing.
  useEffect(() => {
    if (loading || sucio) return;
    setEquipos(config.equipos.map((e) => ({ ...e, miembros: [...(e.miembros || [])] })));
    setFlujos(config.flujos.map((fl) => ({ ...fl, fases: fl.fases.map((f) => ({ ...f })) })));
    setCoordinadores([...(config.coordinadores || [])]);
    setAprobadores([...(config.aprobadores || [])]);
  }, [config, loading, sucio]);

  const marcar = () => { setSucio(true); setEstado('pendiente'); setError(null); };

  // AUTOSAVE. The first version had a Guardar button; Fran added tipos, opened
  // the dialog and saw none of them — nothing had been written. Like the
  // Notificaciones grid, every change persists on its own, debounced so a
  // name being typed is written once. Invalid states (a blank name, an empty
  // flow) wait, with the reason shown, until they are valid.
  useEffect(() => {
    if (!sucio) return undefined;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      const limpio = {
        equipos: equipos.map((e) => ({ id: e.id, nombre: e.nombre.trim(), miembros: e.miembros || [] })),
        flujos: flujos.map((fl) => ({
          id: fl.id,
          nombre: fl.nombre.trim(),
          fases: fl.fases.map((f) => ({ id: f.id, nombre: f.nombre.trim(), equipoId: f.equipoId || null, diasObjetivo: f.diasObjetivo || null, requiereAprobacion: Boolean(f.requiereAprobacion) })),
        })),
        coordinadores,
        aprobadores,
      };
      const msg = validarConfigOt(limpio);
      if (msg) { setError(msg); setEstado('pendiente'); return; }
      setEstado('guardando');
      try {
        await guardar(limpio);
        setEstado('guardado');
        setError(null);
        try {
          await logBitacora({
            action: 'UPDATE',
            collection: 'orgs',
            documentId: userData.org,
            user,
            userData,
            metadata: {
              description: `Configuró órdenes de trabajo: ${limpio.equipos.length} equipos, ${limpio.flujos.length} tipos (${limpio.flujos.map((fl) => fl.nombre).join(', ')})`,
            },
          });
        } catch (e) { console.error('bitacora config ot failed', e); }
      } catch (e) {
        console.error('guardar config ot failed', e);
        setEstado('error');
        setError(e.message || 'No se pudo guardar.');
      }
    }, 700);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [sucio, equipos, flujos, coordinadores, aprobadores, guardar, user, userData]);

  const agregarEquipo = () => {
    const n = nuevoEquipo.trim();
    if (!n) return;
    setEquipos((prev) => [...prev, { id: idDesdeNombre(n, prev.map((e) => e.id)), nombre: n, miembros: [] }]);
    setNuevoEquipo('');
    marcar();
  };
  const quitarEquipo = (id) => {
    setEquipos((prev) => prev.filter((e) => e.id !== id));
    setFlujos((prev) => prev.map((fl) => ({
      ...fl, fases: fl.fases.map((f) => (f.equipoId === id ? { ...f, equipoId: null } : f)),
    })));
    marcar();
  };
  const renombrarEquipo = (id, nombre) => { setEquipos((prev) => prev.map((e) => (e.id === id ? { ...e, nombre } : e))); marcar(); };
  const setMiembros = (id, uids) => { setEquipos((prev) => prev.map((e) => (e.id === id ? { ...e, miembros: uids } : e))); marcar(); };
  const patchFlujo = (id, cambios) => { setFlujos((prev) => prev.map((fl) => (fl.id === id ? { ...fl, ...cambios } : fl))); marcar(); };
  const quitarFlujo = (id) => { setFlujos((prev) => prev.filter((fl) => fl.id !== id)); marcar(); };
  const agregarFlujo = () => {
    const n = nuevoTipo.trim();
    if (!n) return;
    setFlujos((prev) => [...prev, {
      id: idDesdeNombre(n, prev.map((fl) => fl.id)),
      nombre: n,
      fases: [{ id: 'solicitud', nombre: 'Solicitud', equipoId: null, diasObjetivo: null }],
    }]);
    setNuevoTipo('');
    marcar();
  };
  // Open or closed, an OT of a tipo pins it: deleting the flow would leave
  // the OT with no fases to show.
  const enUsoPor = (id) => (ordenesTrabajo || []).filter((o) => (o.tipo || 'fianza') === id).length;

  if (loading) return <Text className="text-sm text-zinc-500">Cargando…</Text>;

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <Heading level={2}>Órdenes de trabajo</Heading>
        <div className="flex items-center gap-3 text-xs">
          {estado === 'guardando' && <span className="text-zinc-500">Guardando…</span>}
          {estado === 'guardado' && <span className="text-green-700">Guardado</span>}
          {estado === 'pendiente' && !error && <span className="text-zinc-400">Sin guardar</span>}
          {error && <span className={estado === 'error' ? 'text-red-700' : 'text-amber-700'}>{error}</span>}
        </div>
      </div>

      <section className="space-y-3">
        <Heading level={3}>Equipos</Heading>
        {/* overflow-visible, not hidden: the member picker (AsignadosBubbles)
            opens a popover that the table box would otherwise clip. */}
        <div className="bg-white rounded-lg border border-zinc-200 shadow-sm overflow-visible">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-azul-surelia text-left text-xs font-medium text-white">
                <th className="px-3 py-2 rounded-tl-lg">Equipo</th>
                <th className="px-3 py-2">Miembros</th>
                <th className="px-3 py-2 w-12 rounded-tr-lg" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {equipos.map((e) => (
                <tr key={e.id}>
                  <td className="px-3 py-2">
                    <input type="text" value={e.nombre} onChange={(ev) => renombrarEquipo(e.id, ev.target.value)} className={`${field} w-full`} />
                  </td>
                  <td className="px-3 py-2">
                    <AsignadosBubbles
                      uids={e.miembros || []}
                      miembros={miembros}
                      meUid={user?.uid}
                      onChange={(uids) => setMiembros(e.id, uids)}
                      max={6}
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button title="Quitar equipo" onClick={() => quitarEquipo(e.id)} className="p-1.5 rounded hover:bg-zinc-100 text-zinc-400">
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              <tr>
                <td className="px-3 py-2" colSpan={3}>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={nuevoEquipo}
                      onChange={(ev) => setNuevoEquipo(ev.target.value)}
                      onKeyDown={(ev) => { if (ev.key === 'Enter') agregarEquipo(); }}
                      placeholder="Nuevo equipo"
                      className={`${field} w-64`}
                    />
                    <Button outline color="surelia" onClick={agregarEquipo} disabled={!nuevoEquipo.trim()}>Agregar</Button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Who runs the relevo between fases: sees the bandeja, Carga and
          Estadísticas, and is the only one who moves órdenes. Not a usuarios
          role (Fran, 2026-10-19). */}
      <section className="space-y-2">
        <Heading level={3}>Coordinación</Heading>
        <div className="bg-white rounded-lg border border-zinc-200 shadow-sm divide-y divide-zinc-100">
          <div className="px-3 py-2.5 grid grid-cols-[7rem_1fr_auto] items-center gap-3">
            <span className="text-sm text-zinc-700">Coordinadores</span>
            <AsignadosBubbles
              uids={coordinadores}
              miembros={miembros}
              meUid={user?.uid}
              onChange={(uids) => { setCoordinadores(uids); marcar(); }}
              max={8}
            />
            <span className="text-xs text-zinc-500">
              {coordinadores.length === 0 ? 'El owner, mientras no haya' : coordinadores.length}
            </span>
          </div>
          <div className="px-3 py-2.5 grid grid-cols-[7rem_1fr_auto] items-center gap-3">
            <span className="text-sm text-zinc-700">Aprobadores</span>
            <AsignadosBubbles
              uids={aprobadores}
              miembros={miembros}
              meUid={user?.uid}
              onChange={(uids) => { setAprobadores(uids); marcar(); }}
              max={8}
            />
            <span className="text-xs text-zinc-500">
              {aprobadores.length === 0 ? 'Los coordinadores, mientras no haya' : aprobadores.length}
            </span>
          </div>
        </div>
      </section>

      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <Heading level={3}>Tipos de orden</Heading>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={nuevoTipo}
            onChange={(ev) => setNuevoTipo(ev.target.value)}
            onKeyDown={(ev) => { if (ev.key === 'Enter') agregarFlujo(); }}
            placeholder="Nuevo tipo de orden"
            className={`${field} w-64`}
          />
          <Button outline color="surelia" onClick={agregarFlujo} disabled={!nuevoTipo.trim()}>Agregar</Button>
        </div>
      </div>

      {flujos.map((fl) => (
        <TablaFases
          key={fl.id}
          flujo={fl}
          equipos={equipos}
          enUso={enUsoPor(fl.id)}
          onChange={(fases) => patchFlujo(fl.id, { fases })}
          onRename={(nombre) => patchFlujo(fl.id, { nombre })}
          onDelete={() => quitarFlujo(fl.id)}
        />
      ))}
    </div>
  );
};

export default ConfiguracionOrdenesTrabajo;
